import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck, DownloadFileFn} from '../../helpers'
import {
  //#region Task
  SearchTaskClient,
  GetTaskClient,
  DeleteTaskClient,
  UpdateTaskClient,
  CreateTaskClient,
  ArchivingTaskClient,
  UpdateTaskCompletionClient,
  //#endregion

  //#region ActivityReport
  SearchActivityReportClient,
  GetActivityReportClient,
  CreateActivityReportClient,
  UpdateActivityReportClient,
  GetAllTimeKeepingTemplateClient,
  CheckActivityReportClient,
  GetProjectInfoClient,
  GetActivityReportExcelClient,
  ReleaseActivityReportClient,
  //#endregion
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

//#region Task
const archivingTaskClient = new ArchivingTaskClient(SERVICE_URL)
const searchTaskClient = new SearchTaskClient(SERVICE_URL)
const getTaskClient = new GetTaskClient(SERVICE_URL)
const createTaskClient = new CreateTaskClient(SERVICE_URL)
const deleteTaskClient = new DeleteTaskClient(SERVICE_URL)
const updateTaskClient = new UpdateTaskClient(SERVICE_URL)
const updateTaskCompletionClient = new UpdateTaskCompletionClient(SERVICE_URL)
//#endregion

//#region ActivityReport
const searchActivityReportClient = new SearchActivityReportClient(SERVICE_URL)
const getActivityReportClient = new GetActivityReportClient(SERVICE_URL)
const createActivityReportClient = new CreateActivityReportClient(SERVICE_URL)
const updateActivityReportClient = new UpdateActivityReportClient(SERVICE_URL)
const getAllTimeKeepingTemplateClient = new GetAllTimeKeepingTemplateClient(SERVICE_URL)
const checkActivityReportClient = new CheckActivityReportClient(SERVICE_URL)
const getProjectInfoClient = new GetProjectInfoClient(SERVICE_URL)
const getActivityReportExcelClient = new GetActivityReportExcelClient(SERVICE_URL)
const releaseActivityReportClient = new ReleaseActivityReportClient(SERVICE_URL)
//#endregion

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  isArchived: false,
  detail: null,
  checkreport: null,
  checkinfo: null,
  excel: null,
  releaseReport: false,
  task: null,
  activityReport: null,
  timeKeepingTemplate: null,
}

//#region Task
export const taskGetAll = createAsyncThunk('workplan/task/getAll', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await searchTaskClient.search(
      params.projectId,
      params.constructionSiteId,
      params.blockId,
      params.plotId,
      params.taskStateId,
      params.staffId,
      params.onlyRootTask,
      params.name,
      params.startDate,
      params.endDate,
      params.items_per_page,
      params.page,
      params.order,
      params.sort
    )
    dispatch(setTask(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const taskGetId = createAsyncThunk('workplan/task/getId', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await getTaskClient.get(id)
    dispatch(setDetail(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const taskDelete = createAsyncThunk('workplan/task/delete', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await deleteTaskClient.delete(params)
    dispatch(setDeleted(true))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const taskCreate = createAsyncThunk('workplan/task/create', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await createTaskClient.create(params)
    dispatch(setRedirect(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const taskUpdate = createAsyncThunk('workplan/task/update', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await updateTaskClient.update(params)
    dispatch(setRedirect(response || params))
    dispatch(setDetail(params))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setDetail(params))
    dispatch(setError(error))
    throw error
  }
})

export const taskArchiving = createAsyncThunk(
  'workplan/task/archiving',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await archivingTaskClient.archive(params)
      dispatch(setArchived(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const taskExcel = createAsyncThunk(
  'workplan/task/excel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchTaskClient.excel(
        params.projectId,
        params.constructionSiteId,
        params.blockId,
        params.plotId,
        params.taskStateId,
        params.staffId,
        params.name,
        params.startDate,
        params.endDate,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const taskUpdateCompletion = createAsyncThunk(
  'workplan/task/updateCompletion',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateTaskCompletionClient.update(params)
      dispatch(setRedirect(response || params))
      dispatch(setDetail(params))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)
//#endregion

//#region ActivityReport
export const activityReportGetAll = createAsyncThunk(
  'workplan/activityReport/getAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchActivityReportClient.search(
        params.name,
        params.activityReportType,
        params.projectId,
        params.reportDate,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setActivityReport(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const activityReportGetId = createAsyncThunk(
  'workplan/activityReport/getId',
  async (id, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getActivityReportClient.get(id)
      dispatch(setDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const activityReportCreate = createAsyncThunk(
  'workplan/activityReport/create',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createActivityReportClient.create(params)
      dispatch(setRedirect(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const activityReportUpdate = createAsyncThunk(
  'workplan/activityReport/update',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateActivityReportClient.update(params)
      dispatch(setRedirect(response || params))
      dispatch(setDetail(params))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

export const activityReportGetAllTimeKeeping = createAsyncThunk(
  'workplan/activityReport/getAllTimeKeeping',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getAllTimeKeepingTemplateClient.getAll(params)
      dispatch(setTimeKeepingTemplate(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const activityReportCheckReport = createAsyncThunk(
  'workplan/activityReport/checkReport',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await checkActivityReportClient.get(params)
      dispatch(setCheckReport(response || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const activityReportExcel = createAsyncThunk(
  'workplan/activityReport/excel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchActivityReportClient.excel(
        params.name,
        params.activityReportType,
        params.projectId,
        params.reportDate,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )

      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const activityReportGetProjectInformation = createAsyncThunk(
  'workplan/activityReport/getProjectInformation',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getProjectInfoClient.getAll(params)
      dispatch(setCheckInfo(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const activityReportExcelGetId = createAsyncThunk(
  'workplan/activityReportExcelGetId',
  async ({id, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getActivityReportExcelClient.get(id)
      if (response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  }
)

export const activityReportRelease = createAsyncThunk(
  'workplan/activityReport/release',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await releaseActivityReportClient.update(params)
      dispatch(setReleaseReport(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)
//#endregion

// Slice
const workplanSlice = createSlice({
  name: 'workplan',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setTask: (state, action) => {
      state.task = action.payload
      state.loading = false
    },
    setActivityReport: (state, action) => {
      state.activityReport = action.payload
      state.loading = false
    },
    setDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setDeleted: (state, action) => {
      state.isDeleted = action.payload
      state.loading = false
    },
    setRedirect: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setArchived: (state, action) => {
      state.isArchived = action.payload
      state.loading = false
    },
    setExcel: (state, action) => {
      state.excel = action.payload
      state.loading = false
    },
    setCheckReport: (state, action) => {
      state.checkreport = action.payload
      state.loading = false
    },
    setCheckInfo: (state, action) => {
      state.checkinfo = action.payload
      state.loading = false
    },
    setTimeKeepingTemplate: (state, action) => {
      state.timeKeepingTemplate = action.payload
      state.loading = false
    },
    setReleaseReport: (state, action) => {
      state.releaseReport = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('workplan/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

// Export actions
export const {
  setLoading,
  setError,
  setTask,
  setActivityReport,
  setDetail,
  setDeleted,
  setRedirect,
  setArchived,
  setExcel,
  setCheckReport,
  setCheckInfo,
  setTimeKeepingTemplate,
  setReleaseReport,
  resetState,
} = workplanSlice.actions

// Selectors
export const selectWorkplanLoading = (state) => state.workplan.loading
export const selectWorkplanError = (state) => state.workplan.error
export const selectWorkplanTask = (state) => state.workplan.task
export const selectWorkplanActivityReport = (state) => state.workplan.activityReport
export const selectWorkplanDetail = (state) => state.workplan.detail
export const selectWorkplanDeleted = (state) => state.workplan.isDeleted
export const selectWorkplanRedirect = (state) => state.workplan.isRedirect
export const selectWorkplanArchived = (state) => state.workplan.isArchived
export const selectWorkplanExcel = (state) => state.workplan.excel
export const selectWorkplanCheckReport = (state) => state.workplan.checkreport
export const selectWorkplanCheckInfo = (state) => state.workplan.checkinfo
export const selectWorkplanTimeKeepingTemplate = (state) => state.workplan.timeKeepingTemplate
export const selectWorkplanReleaseReport = (state) => state.workplan.releaseReport

export default workplanSlice.reducer
